/**
 * Lightweight perf telemetry client — native APIs only, zero new deps.
 *
 * What it captures (per page visit):
 *   • TTFB  — Time To First Byte (server response speed).
 *   • FCP   — First Contentful Paint (browser shows first pixel).
 *   • LCP   — Largest Contentful Paint (main hero/image visible).
 *   • CLS   — Cumulative Layout Shift (visual jank score).
 *   • INP   — Interaction-to-Next-Paint (responsiveness, P95).
 *   • route_change — SPA route swap timing (mark→render delta).
 *   • slow_render  — any single render >150ms (detail = component/scope hint).
 *
 * Transport:
 *   • Buffers events for ~5s, then ships via /api/perf/log/batch.
 *   • On `pagehide` / `visibilitychange→hidden` flushes via navigator.sendBeacon
 *     so we never lose a sample to a tab close.
 *
 * Sampling: 100% of users for now — payload is tiny (≈40 events / 30 days TTL).
 *
 * Usage:
 *   import { initPerfTelemetry, reportSlowRender } from './lib/perfTelemetry';
 *   initPerfTelemetry();        // call once at app boot
 *   reportSlowRender('VisionBoard', 187); // optional manual hook
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${API_URL}/api/perf/log/batch`;
const FLUSH_INTERVAL_MS = 5000;
const BUFFER_LIMIT = 30;
const SLOW_RENDER_THRESHOLD_MS = 150;

let buffer = [];
let flushTimer = null;
let initialised = false;
let lastPath = '';

const currentPath = () => {
  if (typeof window === 'undefined') return '';
  return window.location.pathname || '';
};

const enqueue = (metric, value, detail) => {
  if (typeof value !== 'number' || !isFinite(value)) return;
  buffer.push({
    path: currentPath().slice(0, 200),
    metric,
    value: Math.round(value * 100) / 100,
    ...(detail ? { detail: String(detail).slice(0, 120) } : {}),
  });
  if (buffer.length >= BUFFER_LIMIT) flush();
};

const flush = (useBeacon = false) => {
  if (!buffer.length) return;
  const events = buffer.splice(0, buffer.length);
  const body = JSON.stringify({ events });
  try {
    if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
    const token = typeof window !== 'undefined' ? localStorage.getItem('reclaim_token') : null;
    fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body,
      keepalive: true,
    }).catch(() => { /* fire-and-forget */ });
  } catch (_) { /* noop */ }
};

const startFlushLoop = () => {
  if (flushTimer) return;
  flushTimer = setInterval(() => flush(false), FLUSH_INTERVAL_MS);
};

// ---------- Web Vitals via PerformanceObserver --------------------------

const observePaint = () => {
  try {
    const obs = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.name === 'first-contentful-paint') {
          enqueue('fcp', entry.startTime);
        }
      });
    });
    obs.observe({ type: 'paint', buffered: true });
  } catch (_) { /* not supported */ }
};

const observeLCP = () => {
  try {
    let lastEntry = null;
    const obs = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      if (entries.length) lastEntry = entries[entries.length - 1];
    });
    obs.observe({ type: 'largest-contentful-paint', buffered: true });
    // Lock-in the LCP value when the page is hidden (LCP is final at that point).
    const lockIn = () => {
      if (lastEntry) {
        enqueue('lcp', lastEntry.startTime || lastEntry.renderTime || 0);
        lastEntry = null;
        try { obs.disconnect(); } catch (_) { /* noop */ }
      }
    };
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') lockIn();
    }, { once: false });
    window.addEventListener('pagehide', lockIn, { once: true });
  } catch (_) { /* not supported */ }
};

const observeCLS = () => {
  try {
    let cls = 0;
    const obs = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (!entry.hadRecentInput) cls += entry.value;
      });
    });
    obs.observe({ type: 'layout-shift', buffered: true });
    const lockIn = () => {
      enqueue('cls', cls);
      cls = 0;
      try { obs.disconnect(); } catch (_) { /* noop */ }
    };
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') lockIn();
    }, { once: false });
    window.addEventListener('pagehide', lockIn, { once: true });
  } catch (_) { /* not supported */ }
};

const observeINP = () => {
  try {
    const obs = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        // Only sample slow interactions to keep volume sane.
        if (entry.duration >= 100) enqueue('inp', entry.duration, entry.name);
      });
    });
    obs.observe({ type: 'event', buffered: true, durationThreshold: 100 });
  } catch (_) { /* not supported */ }
};

const observeLongTasks = () => {
  try {
    const obs = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (entry.duration >= SLOW_RENDER_THRESHOLD_MS) {
          enqueue('slow_render', entry.duration, 'longtask');
        }
      });
    });
    obs.observe({ type: 'longtask', buffered: true });
  } catch (_) { /* not supported */ }
};

const captureTTFB = () => {
  try {
    const nav = performance.getEntriesByType('navigation')[0];
    if (nav && nav.responseStart) enqueue('ttfb', nav.responseStart);
  } catch (_) { /* noop */ }
};

const captureRouteChange = () => {
  try {
    const path = currentPath();
    if (lastPath && lastPath !== path) {
      // Mark→tick delta proxies SPA route render budget.
      const t = performance.now();
      requestAnimationFrame(() => {
        const delta = performance.now() - t;
        enqueue('route_change', delta, path);
      });
    }
    lastPath = path;
  } catch (_) { /* noop */ }
};

// ---------- Public API --------------------------------------------------

export const initPerfTelemetry = () => {
  if (initialised || typeof window === 'undefined' || !('PerformanceObserver' in window)) return;
  initialised = true;
  lastPath = currentPath();
  captureTTFB();
  observePaint();
  observeLCP();
  observeCLS();
  observeINP();
  observeLongTasks();
  startFlushLoop();
  // Final flush on unload.
  window.addEventListener('pagehide', () => flush(true), { once: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
  // Patch history so single-page route changes get sampled.
  const _push = window.history.pushState;
  const _replace = window.history.replaceState;
  window.history.pushState = function patchedPush(...args) {
    _push.apply(this, args);
    captureRouteChange();
  };
  window.history.replaceState = function patchedReplace(...args) {
    _replace.apply(this, args);
    captureRouteChange();
  };
  window.addEventListener('popstate', captureRouteChange);
};

export const reportSlowRender = (componentName, durationMs) => {
  enqueue('slow_render', durationMs, componentName);
};

export const reportPageLoad = (durationMs) => enqueue('page_load', durationMs);

export default initPerfTelemetry;
